import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import api from '../services/api';
import { loadThree } from '../utils/threeLoader';
import './BlogPage.css';

const BlogPage = () => {
  const { t } = useTranslation();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const liquidAppRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Nettoyer l'instance existante avant d'en créer une nouvelle
    if (liquidAppRef.current && liquidAppRef.current.dispose) {
      liquidAppRef.current.dispose();
      liquidAppRef.current = null;
    }

    let isMounted = true;

    const loadLiquidBackground = async () => {
      try {
        // S'assurer que Three.js est chargé avant de charger la bibliothèque
        await loadThree();
        
        const LiquidBackground = (await import('https://cdn.jsdelivr.net/npm/threejs-components@0.0.27/build/backgrounds/liquid1.min.js')).default;
        
        // Vérifier isMounted AVANT de créer l'instance
        if (!isMounted) {
          return;
        }
        
        const app = LiquidBackground(canvas);
        
        // Vérifier à nouveau isMounted après création
        if (!isMounted) {
          if (app && app.dispose) {
            app.dispose();
          }
          return;
        }
        
        liquidAppRef.current = app;
        app.loadImage('/obsidienne-soulages.jpg');
        app.liquidPlane.material.metalness = 0.75;
        app.liquidPlane.material.roughness = 0.25;
        app.liquidPlane.uniforms.displacementScale.value = 5;
        app.setRain(false);
      } catch (error) {
        console.error('Error loading LiquidBackground:', error);
      }
    };

    loadLiquidBackground();

    return () => {
      isMounted = false;
      
      // Nettoyer l'app depuis le ref (fonctionne même si l'app n'est pas encore créée)
      if (liquidAppRef.current && liquidAppRef.current.dispose) {
        liquidAppRef.current.dispose();
        liquidAppRef.current = null;
      }
    };
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      // L'API est publique, pas besoin d'authentification pour la lecture
      const response = await api.get('/blog/');
      setBlogPosts(response.data.results || response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="blog-page">
      <canvas ref={canvasRef} id="canvas" className="blog-canvas"></canvas>
      <Navbar />
      <main className="blog-main">
        <div className="blog-container">
          <h1 className="blog-title">Art Blog</h1>
          <h2 className="blog-subtitle">{t('blog.subtitle')}</h2>
          
          {loading && (
            <div className="blog-loading">
              <p>Chargement...</p>
            </div>
          )}

          {error && (
            <div className="blog-error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && blogPosts.length === 0 && (
            <div className="blog-empty">
              <p>Aucun article pour le moment.</p>
            </div>
          )}

          {!loading && !error && blogPosts.length > 0 && (
            <div className="blog-grid">
              {blogPosts.map((post) => (
                <article key={post.id} className="blog-card">
                  <div className="blog-video-container">
                    <video
                      className="blog-video"
                      controls
                      preload="metadata"
                      poster=""
                    >
                      <source src={post.video_url} type="video/mp4" />
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  </div>
                  <div className="blog-card-content">
                    <h2 className="blog-card-title">{post.titre}</h2>
                    <p className="blog-card-description">{post.description}</p>
                    <time className="blog-card-date">
                      {new Date(post.date_creation).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </time>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlogPage;

